// Loudness of one analyser frame, 0..1. Samples are unsigned bytes centred
// on 128 (AnalyserNode.getByteTimeDomainData). RMS, then a gentle curve so
// normal speech fills the bars instead of hugging the baseline.
export function levelFromTimeDomain(samples: Uint8Array): number {
    if (!samples.length) {
        return 0;
    }
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
        const v = (samples[i] - 128) / 128;
        sum += v * v;
    }
    return shapeLevel(Math.sqrt(sum / samples.length));
}

// Map an RMS value (0..1) to a bar height (0..1): a gentle curve so normal
// speech fills the bars instead of hugging the baseline. Shared by the live
// view and playback so the two look the same.
export function shapeLevel(rms: number): number {
    return Math.min(1, Math.sqrt(rms) * 1.4);
}

// Raw RMS of each of `count` equal slices (not normalised), for playback bars.
export function rmsSeries(samples: Float32Array, count: number): number[] {
    const out = new Array(count).fill(0);
    const size = samples.length / count;
    for (let b = 0; b < count; b++) {
        const start = Math.floor(b * size);
        const end = Math.max(start + 1, Math.floor((b + 1) * size));
        let sum = 0, n = 0;
        for (let i = start; i < end && i < samples.length; i++) { sum += samples[i] * samples[i]; n++; }
        out[b] = n ? Math.sqrt(sum / n) : 0;
    }
    return out;
}

// Rolling history of levels: drop the oldest, append the newest.
export function pushLevel(history: number[], level: number): number[] {
    return [...history.slice(1), level];
}

// Bar heights for a whole recording: RMS of each of `bars` equal slices of
// the decoded samples (-1..1), normalised so the loudest bar is 1.
export function peaksFromSamples(samples: Float32Array, bars: number): number[] {
    const out = new Array(bars).fill(0);
    if (!samples.length) {
        return out;
    }
    const size = samples.length / bars;
    for (let b = 0; b < bars; b++) {
        const start = Math.floor(b * size);
        const end = Math.max(start + 1, Math.floor((b + 1) * size));
        let sum = 0;
        let n = 0;
        for (let i = start; i < end && i < samples.length; i++) {
            sum += samples[i] * samples[i];
            n++;
        }
        out[b] = n ? Math.sqrt(sum / n) : 0;
    }
    const max = Math.max(...out);
    return max > 0 ? out.map(v => v / max) : out;
}

// The `size` bars ending at index `head` (inclusive) of a per-bar series,
// right-aligned and padded with silence before the start. Used to scroll a
// recording's bars past a fixed window during playback.
export function windowAt(series: number[], head: number, size: number): number[] {
    const end = Math.min(head, series.length - 1);
    const out = new Array(size).fill(0);
    for (let k = 0; k < size; k++) {
        const i = end - (size - 1 - k);
        out[k] = i >= 0 && i < series.length ? series[i] : 0;
    }
    return out;
}
